import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  getLibraryRagGuideline,
  updateLibraryRagGuideline,
} from "../api/rag-api.js";
import { formatRelativeTime } from "../api/mappers.js";

const RAG_GUIDELINE_MAX_LENGTH = 5000;

interface LibraryGuidelinesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  library: { id: string; name: string } | null;
}

export function LibraryGuidelinesDialog({
  open,
  onOpenChange,
  library,
}: LibraryGuidelinesDialogProps) {
  const [guideline, setGuideline] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !library) {
      return;
    }

    const libraryId = library.id;
    let cancelled = false;

    async function loadGuideline() {
      setLoading(true);
      setError(null);
      try {
        const data = await getLibraryRagGuideline(libraryId);
        if (cancelled) return;
        setGuideline(data.guideline ?? "");
        setUpdatedAt(data.updatedAt);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load guidelines");
          setGuideline("");
          setUpdatedAt(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadGuideline();

    return () => {
      cancelled = true;
    };
  }, [open, library]);

  async function handleSave() {
    if (!library) return;

    const trimmed = guideline.trim();
    if (trimmed.length > RAG_GUIDELINE_MAX_LENGTH) {
      setError(`Guidelines must be ${RAG_GUIDELINE_MAX_LENGTH} characters or fewer.`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const data = await updateLibraryRagGuideline(library.id, trimmed.length > 0 ? trimmed : null);
      setGuideline(data.guideline ?? "");
      setUpdatedAt(data.updatedAt);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save guidelines");
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    if (!library) return;

    setSaving(true);
    setError(null);
    try {
      const data = await updateLibraryRagGuideline(library.id, null);
      setGuideline("");
      setUpdatedAt(data.updatedAt);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to clear guidelines");
    } finally {
      setSaving(false);
    }
  }

  const charCount = guideline.length;
  const overLimit = charCount > RAG_GUIDELINE_MAX_LENGTH;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-glass-border bg-[#111b35] text-white sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Library guidelines</DialogTitle>
          <DialogDescription className={`text-slate-400 ${pageDescriptionMobileHiddenClassName}`}>
            {library
              ? `Supplemental instructions for KM chat in "${library.name}". Combined with workspace guidelines. These guide tone and focus but do not override citations or source grounding.`
              : "Supplemental instructions for KM chat in this library. Combined with workspace guidelines."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label htmlFor="library-rag-guideline" className="text-slate-400">
                Guidelines
              </Label>
              <Textarea
                id="library-rag-guideline"
                placeholder="e.g. Prefer concise bullet points. Focus on this library's source set."
                value={guideline}
                onChange={(event) => setGuideline(event.target.value)}
                rows={10}
                className="min-h-[220px] resize-y border-white/10 bg-white/5 text-white placeholder:text-slate-500"
              />
              <div className="flex items-center justify-between text-xs">
                <span className={overLimit ? "text-red-400" : "text-slate-500"}>
                  {charCount} / {RAG_GUIDELINE_MAX_LENGTH}
                </span>
                {updatedAt ? (
                  <span className="text-slate-500">
                    Last saved {formatRelativeTime(updatedAt)}
                  </span>
                ) : null}
              </div>
            </div>
            {error ? <p className="text-sm text-red-400">{error}</p> : null}
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          <div>
            {guideline.trim().length > 0 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleClear()}
                disabled={loading || saving}
              >
                Clear
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => void handleSave()}
              disabled={loading || saving || overLimit}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              Save guidelines
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
